import { useRef } from "react";
import "./style.scss";
import tablette from "../../../assets/main/home/tablette.png";
import tabBlancFR from "../../../assets/main/home/tab_blanc.png";
import tabBlancEN from "../../../assets/main/home/tab_blanc_en.png";
import {
  CogIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  PaintBrushIcon,
  AcademicCapIcon,
  ShieldExclamationIcon,
  PresentationChartBarIcon,
} from "@heroicons/react/24/outline";
import laptopScena from "../../../assets/main/home/laptopScena.png";
import scena_demo from "../../../assets/main/home/scena_demo.jpg";
import { Link } from "react-router-dom";
import VideoPlayer from "./VideoPlayer";
import scenaVideo from "../../../assets/main/home/Intro_Scena.mp4";
import scenaVideoEN from "../../../assets/main/home/Intro_Scena-ENG.mp4";
import iconPause from "../../../assets/main/home/icon_pause.png";
import { useTranslation, Trans } from "react-i18next";
import i18n from "../../i18n";
import { useVideoScrollEffect } from "./hooks/useVideoScrollEffect";

const Home = () => {
  const { t } = useTranslation();

  const videoElement = useRef(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const { isPlaying, togglePlay, hasUserInteracted } =
    VideoPlayer(videoElement);

  // Hook pour l'effet de scroll sur la vidéo
  const { width, borderRadius } = useVideoScrollEffect(videoContainerRef, {
    startOffset: 0, // Commencer immédiatement après 50% de visibilité
    endOffset: 600, // Distance plus longue pour un effet plus lent
    minWidth: 85,
    maxBorderRadius: 32,
  });

  return (
    <section className="homeSection">
      <div className="homeSection__tablette">
        <div className="homeSectionTablette__image">
          <img src={tablette} alt="" />
          <div className="homeSectionTabletteImage__wrapper">
            <div className="homeSectionTablette__title">
              <h2>{t("home.title")},</h2>
              <p>{t("home.subtitle")}</p>
            </div>
          </div>
        </div>
        <div className="homeSectionTablette__imageHigh">
          {i18n.language === "fr" ? (
            <img src={tabBlancFR} alt="" />
          ) : (
            <img src={tabBlancEN} alt="" />
          )}
        </div>
      </div>
      <section className="homeSection__presentation">
        <div className="homeSectionPresentation__header">
          <div className="homeSectionPresentationHeader__titleContainer">
            <div className="homeSectionPresentationHeaderTitleContainer__bar"></div>
            <div className="homeSectionPresentationHeaderTitleContainer__title">
              <h2>
                <Trans
                  i18nKey="home.presentation.section_title"
                  components={{
                    2: <span className="highlight-word" />,
                  }}
                />
              </h2>
            </div>
          </div>
        </div>
        <div className="homeSectionPresentation__content">
          <div className="homeSectionPresentationContent__partLeft">
            <div className="homeSectionPresentationContentPartLeft__informations">
              <div className="homeSectionPresentationContentPartLeftInformations__column">
                <div className="homeSectionPresentationContentPartLeftInformationsColumn__card">
                  <div className="homeSectionPresentationContentPartLeftInformationsColumnCard__text">
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__title">
                      <CogIcon className="homeSectionPresentationContentPartLeftInformationsColumnCardText__icon icon-blue" />
                      <h3>{t("home.presentation.main.title")}</h3>
                    </div>
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__details">
                      <ul>
                        <li>{t("home.presentation.main.text1")}</li>
                        <li>{t("home.presentation.main.text2")}</li>
                        <li>{t("home.presentation.main.text3")}</li>
                        <li>{t("home.presentation.main.text4")}</li>
                        <li>{t("home.presentation.main.text5")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="homeSectionPresentationContentPartLeftInformationsColumn__card">
                  <div className="homeSectionPresentationContentPartLeftInformationsColumnCard__text">
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__title">
                      <PaintBrushIcon className="homeSectionPresentationContentPartLeftInformationsColumnCardText__icon icon-green" />
                      <h3>{t("home.presentation.custom.title")}</h3>
                    </div>
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__details">
                      <ul>
                        <li>{t("home.presentation.custom.text1")}</li>
                        <li>{t("home.presentation.custom.text2")}</li>
                        <li>{t("home.presentation.custom.text3")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="homeSectionPresentationContentPartLeftInformations__column">
                <div className="homeSectionPresentationContentPartLeftInformationsColumn__card">
                  <div className="homeSectionPresentationContentPartLeftInformationsColumnCard__text">
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__title">
                      <ComputerDesktopIcon className="homeSectionPresentationContentPartLeftInformationsColumnCardText__icon icon-purple" />
                      <h3>{t("home.presentation.app.title")}</h3>
                    </div>
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__details">
                      <p data-os="windows">
                        {t("home.presentation.app.text1")}
                      </p>
                      <p data-os="macos">{t("home.presentation.app.text2")}</p>
                      <p data-os="linux">{t("home.presentation.app.text3")}</p>
                    </div>
                  </div>
                </div>
                <div className="homeSectionPresentationContentPartLeftInformationsColumn__card">
                  <div className="homeSectionPresentationContentPartLeftInformationsColumnCard__text">
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__title">
                      <DocumentTextIcon className="homeSectionPresentationContentPartLeftInformationsColumnCardText__icon icon-orange" />
                      <h3>{t("home.presentation.format.title")}</h3>
                    </div>
                    <div className="homeSectionPresentationContentPartLeftInformationsColumnCardText__details">
                      <ul>
                        <li>{t("home.presentation.format.text1")}</li>
                        <li>{t("home.presentation.format.text2")}</li>
                        <li>{t("home.presentation.format.text3")}</li>
                        <li>{t("home.presentation.format.text4")}</li>
                        <li>{t("home.presentation.format.text5")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="homeSectionPresentationContent__laptopImg">
            <img src={laptopScena} alt="laptopScena" />
          </div>
        </div>
        <div className="homeSectionPresentation__button">
          {i18n.language === "fr" ? (
            <a href="/scena_brochure_fr.pdf" target="_blank">
              {t("home.button.download")}
            </a>
          ) : (
            <a href="/scena_brochure_en.pdf" target="_blank">
              {t("home.button.download")}
            </a>
          )}
        </div>
      </section>
      <div
        className="homeSection__player"
        ref={videoContainerRef}
        onClick={togglePlay}
        style={{
          width: `${width}%`,
          borderRadius: `${borderRadius}px`,
        }}
      >
        <video
          className="homeSectionPlayer__video"
          ref={videoElement}
          src={i18n.language === "fr" ? scenaVideo : scenaVideoEN}
          loop
          muted
          autoPlay
          style={{
            borderRadius: `${borderRadius}px`,
          }}
        />
        {hasUserInteracted && !isPlaying && (
          <div className="homeSectionPlayer__button">
            <img src={iconPause} alt="icon pause" className="buttonPause" />
          </div>
        )}
      </div>
      <section className="homeSection__description">
        <div className="homeSectionDescription__header">
          <div className="homeSectionDescriptionHeader__titleContainer">
            <div className="homeSectionDescriptionHeaderTitleContainer__bar"></div>
            <div className="homeSectionDescriptionHeaderTitleContainer__title">
              <h2>
                <Trans
                  i18nKey="home.description.section_title"
                  components={{
                    2: <span className="highlight-word" />,
                  }}
                />
              </h2>
            </div>
          </div>
        </div>
        <div className="homeSectionDescription__content">
          <div className="homeSectionDescription__topContent">
            <div className="homeSectionDescription__contentWrapper">
              <div className="homeSectionDescription__leftColumn">
                <img src={scena_demo} alt="scena illustration" />
              </div>
              <div className="homeSectionDescription__rightColumn">
                <div className="homeSectionDescriptionContent__allCards">
                  <div className="homeSectionDescriptionContent__topCards">
                    <div className="homeSectionDescriptionContentCards__card">
                      <div className="homeSectionDescriptionContentCardsCard__text">
                        <p>
                          <Trans
                            i18nKey="home.description.paragraph5"
                            components={[<strong />]}
                          />
                        </p>
                        <p>
                          <Trans
                            i18nKey="home.description.paragraph6"
                            components={[<strong />]}
                          />
                        </p>
                      </div>
                    </div>
                    <div className="homeSectionDescriptionContentCards__card">
                      <div className="homeSectionDescriptionContentCardsCard__text">
                        <p>
                          <Trans
                            i18nKey="home.description.paragraph3"
                            components={[<strong />]}
                          />
                        </p>
                        <p>
                          <Trans
                            i18nKey="home.description.paragraph4"
                            components={[<strong />]}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="homeSectionDescriptionContentCards__card">
                    <div className="homeSectionDescriptionContentCardsCard__text">
                      <p>
                        <Trans
                          i18nKey="home.description.paragraph1"
                          components={[<strong />]}
                        />
                      </p>
                      <p>
                        <Trans
                          i18nKey="home.description.paragraph2"
                          components={[<strong />]}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="homeSection__solution">
        <div className="homeSectionSolution__text">
          <h2>
            <Trans
              i18nKey="home.solution.section_title"
              components={{
                2: <span className="highlight-word" />,
              }}
            />
          </h2>
        </div>
        <div className="homeSectionSolution__differentSolution">
          <div className="homeSectionSolutionDifferentSolution__scena">
            <div className="homeSectionSolutionDifferentSolutionScena__icon">
              <AcademicCapIcon className="icon-blue" />
              <h4>{t("home.solution.for.title")}</h4>
            </div>
            <div className="homeSectionSolutionDifferentSolutionScena__text">
              <p>{t("home.solution.for.text1")}</p>
              <br />
              <p>{t("home.solution.for.text2")}</p>
              <br />
              <p>{t("home.solution.for.text3")}</p>
              <br />
              <p>{t("home.solution.for.text4")}</p>
            </div>
          </div>
          <div className="homeSectionSolutionDifferentSolution__scena">
            <div className="homeSectionSolutionDifferentSolutionScena__icon">
              <ShieldExclamationIcon className="icon-red" />
              <h4>{t("home.solution.ops.title")}</h4>
            </div>
            <div className="homeSectionSolutionDifferentSolutionScena__text">
              <p>{t("home.solution.ops.text1")}</p>
              <br />
              <p>{t("home.solution.ops.text2")}</p>
              <br />
              <p>{t("home.solution.ops.text3")}</p>
            </div>
          </div>
          <div className="homeSectionSolutionDifferentSolution__scena">
            <div className="homeSectionSolutionDifferentSolutionScena__icon">
              <PresentationChartBarIcon className="icon-yellow" />
              <h4>{t("home.solution.com.title")}</h4>
            </div>
            <div className="homeSectionSolutionDifferentSolutionScena__text">
              <p>{t("home.solution.com.text1")}</p>
              <br />
              <p>{t("home.solution.com.text2")}</p>
              <br />
              <p>{t("home.solution.com.text3")}</p>
              <br />
              <p>{t("home.solution.com.text4")}</p>
            </div>
          </div>
        </div>
        <div className="homeSectionSolution__buttons">
          <div className="homeSection__button">
            <Link className="homeSectionButton__link" to="/contact">
              {t("home.button.try")}
            </Link>
          </div>
        </div>
      </section>
    </section>
  );
};

export default Home;
